/**
 * 霓虹蛇境 (Neon Snake Realm) - 核心类型定义
 * 
 * 本文件定义了游戏中所有核心数据结构、枚举和接口。
 * 所有类型均遵循严格模式，不使用 any 类型。
 */

// ============================================================================
// 基础几何类型
// ============================================================================

/** 二维坐标位置 */
export interface Position {
  /** X 轴坐标（网格列索引） */
  x: number;
  /** Y 轴坐标（网格行索引） */
  y: number;
}

/** 移动方向向量 */
export interface Direction {
  /** X 轴方向分量：-1（左）、0（不动）、1（右） */
  x: number;
  /** Y 轴方向分量：-1（上）、0（不动）、1（下） */
  y: number;
}

// ============================================================================
// 游戏状态枚举
// ============================================================================

/** 游戏运行状态 */
export enum GameStatus {
  /** 等待开始（初始状态） */
  Idle = 'idle',
  /** 游戏进行中 */
  Playing = 'playing',
  /** 游戏暂停 */
  Paused = 'paused',
  /** 游戏结束 */
  GameOver = 'gameover',
}

/** 特殊道具类型 */
export enum PowerUpType {
  /** 加速药水：蛇移动速度提升 50%，持续 8 秒 */
  Speed = 'speed',
  /** 减速药水：蛇移动速度降低 30%，持续 8 秒 */
  Slow = 'slow',
  /** 穿墙护盾：蛇可穿越墙壁从对面出现，持续 10 秒 */
  WallPass = 'wallPass',
  /** 双倍分数：吃到食物得分翻倍，持续 10 秒 */
  DoubleScore = 'doubleScore',
    /** 缩小药剂：蛇身缩短 3 节（最少保留 3 节），立即生效 */
  Shrink = 'shrink',
  /** 清除障碍物：立即随机清除场景中10个障碍物，立即生效 */
  ClearObstacles = 'clearObstacles',
}

// ============================================================================
// 游戏实体接口
// ============================================================================

/** 蛇 */
export interface Snake {
  /** 蛇身坐标数组，索引 0 为头部 */
  body: Position[];
  /** 当前实际移动方向 */
  direction: Direction;
  /** 缓冲的下一个方向（用于防止反向移动和快速输入） */
  nextDirection: Direction;
}

/** 普通食物 */
export interface Food {
  /** 食物所在网格位置 */
  position: Position;
  /** 食物类型标识 */
  type: 'normal';
}

/** 场景中的特殊道具实体 */
export interface PowerUpItem {
  /** 道具所在网格位置 */
  position: Position;
  /** 道具类型 */
  type: PowerUpType;
  /** 道具生成时间戳（毫秒），用于计算过期 */
  spawnTime: number;
}

/** 障碍物 */
export interface Obstacle {
  /** 障碍物所在网格位置 */
  position: Position;
}

/** 当前激活的道具效果 */
export interface ActiveEffect {
  /** 效果类型 */
  type: PowerUpType;
  /** 剩余持续时间（毫秒） */
  remainingTime: number;
}

// ============================================================================
// 粒子系统类型
// ============================================================================

/** 单个粒子 */
export interface Particle {
  /** 当前 X 坐标（像素） */
  x: number;
  /** 当前 Y 坐标（像素） */
  y: number;
  /** X 轴速度（像素/秒） */
  vx: number;
  /** Y 轴速度（像素/秒） */
  vy: number;
  /** 当前剩余生命（毫秒） */
  life: number;
  /** 最大生命值（毫秒） */
  maxLife: number;
  /** 粒子颜色（CSS 颜色字符串） */
  color: string;
  /** 粒子半径（像素） */
  size: number;
}

// ============================================================================
// 得分系统类型
// ============================================================================

/** 得分状态 */
export interface ScoreState {
  /** 当前得分 */
  current: number;
  /** 历史最高分（从 localStorage 读取） */
  highScore: number;
  /** 当前连击数 */
  combo: number;
  /** 上次吃到食物的时间戳（毫秒），用于连击判定 */
  lastEatTime: number;
}

// ============================================================================
// 网格配置类型
// ============================================================================

/** 游戏网格配置 */
export interface GridConfig {
  /** 网格列数 */
  width: number;
  /** 网格行数 */
  height: number;
  /** 单个单元格像素大小 */
  cellSize: number;
}

// ============================================================================
// 游戏配置常量类型
// ============================================================================

/** 游戏全局可配置常量 */
export interface GameConfig {
  /** 桌面端网格列数 */
  desktopGridWidth: number;
  /** 桌面端网格行数 */
  desktopGridHeight: number;
  /** 移动端网格列数 */
  mobileGridWidth: number;
  /** 移动端网格行数 */
  mobileGridHeight: number;
  /** 初始蛇身长度 */
  initialSnakeLength: number;
  /** 基础移动间隔（毫秒） */
  baseMoveInterval: number;
  /** 加速药水速度倍率 */
  speedMultiplier: number;
  /** 减速药水速度倍率 */
  slowMultiplier: number;
  /** 加速/减速效果持续时间（毫秒） */
  speedEffectDuration: number;
  /** 穿墙护盾持续时间（毫秒） */
  wallPassDuration: number;
  /** 双倍分数持续时间（毫秒） */
  doubleScoreDuration: number;
  /** 缩小药剂缩短节数 */
  shrinkAmount: number;
  /** 蛇身最小保留节数 */
  minSnakeLength: number;
  /** 普通食物基础得分 */
  foodScore: number;
  /** 连击判定时间窗口（毫秒） */
  comboWindow: number;
  /** 连击额外加分基数 */
  comboBonusBase: number;
  /** 道具生成间隔（毫秒） */
  powerUpSpawnInterval: number;
  /** 道具存在时间（毫秒），超时自动消失 */
  powerUpLifetime: number;
  /** 同时激活道具效果上限 */
  maxActiveEffects: number;
  /** 粒子同时存在数量上限（桌面端） */
  maxParticlesDesktop: number;
  /** 粒子同时存在数量上限（移动端） */
  maxParticlesMobile: number;
  /** 吃到食物时生成的粒子数量范围 [最小值, 最大值] */
  eatParticleCount: [number, number];
  /** 粒子生命周期范围（毫秒） [最小值, 最大值] */
  particleLifeRange: [number, number];
  /** 粒子初始速度范围（像素/秒） [最小值, 最大值] */
  particleSpeedRange: [number, number];
    /** 移动端断点宽度（像素） */
  mobileBreakpoint: number;
  /** 初始生命数 */
  initialLives: number;
  /** 每次吃食物生成的障碍物数量 */
  obstaclesPerEat: number;
  /** 障碍物生成最大尝试次数 */
  obstacleSpawnMaxAttempts: number;
}

// ============================================================================
// 游戏状态聚合类型
// ============================================================================

/** 完整游戏状态 */
export interface GameState {
  /** 游戏运行状态 */
  status: GameStatus;
  /** 蛇实体 */
  snake: Snake;
  /** 当前食物 */
  food: Food;
  /** 场景中的道具（可为 null 表示无道具） */
  powerUpItem: PowerUpItem | null;
  /** 当前激活的道具效果列表 */
  activeEffects: ActiveEffect[];
  /** 得分状态 */
  score: ScoreState;
  /** 网格配置 */
  grid: GridConfig;
    /** 活跃粒子列表 */
  particles: Particle[];
  /** 场景中的障碍物列表 */
  obstacles: Obstacle[];
  /** 剩余生命数 */
  lives: number;
}

// ============================================================================
// Reducer Action 联合类型
// ============================================================================

/** 开始游戏 Action */
export interface StartGameAction {
  type: 'START_GAME';
}

/** 设置方向 Action */
export interface SetDirectionAction {
  type: 'SET_DIRECTION';
  /** 新的移动方向 */
  direction: Direction;
}

/** 游戏时钟滴答 Action */
export interface TickAction {
  type: 'TICK';
  /** 自上一帧以来的时间增量（毫秒） */
  deltaTime: number;
}

/** 重置游戏 Action */
export interface ResetAction {
  type: 'RESET';
}

/** 更新粒子系统 Action */
export interface UpdateParticlesAction {
  type: 'UPDATE_PARTICLES';
  /** 时间增量（毫秒） */
  deltaTime: number;
}

/** 生成道具 Action */
export interface SpawnPowerUpAction {
  type: 'SPAWN_POWERUP';
  /** 新生成的道具 */
  powerUp: PowerUpItem;
}

/** 收集道具 Action */
export interface CollectPowerUpAction {
  type: 'COLLECT_POWERUP';
}

/** 更新道具效果计时器 Action */
export interface UpdateEffectsAction {
  type: 'UPDATE_EFFECTS';
  /** 时间增量（毫秒） */
  deltaTime: number;
}

/**
 * 游戏状态 Reducer 的 Action 联合类型
 * 使用可辨识联合（Discriminated Union）确保类型安全
 */
export type GameAction =
  | StartGameAction
  | SetDirectionAction
  | TickAction
  | ResetAction
  | UpdateParticlesAction
  | SpawnPowerUpAction
  | CollectPowerUpAction
  | UpdateEffectsAction;